/**
 * AutoScheduleSection - Settings for automatic go-live and YouTube-based end-live scheduling
 */
import { Clock, Zap, Youtube, FlaskConical } from "lucide-react";

export const AutoScheduleSection = ({ formData, setFormData }) => {
  const autoGoLive = formData.auto_go_live === true;
  const autoEndEnabled = formData.auto_end_enabled || false;
  const isDummy = formData.is_dummy === true;
  const hasYouTubeUrl = formData.youtube_url && formData.youtube_url.trim() !== '';
  const hasVCloudUrl = formData.vcloud_url && formData.vcloud_url.trim() !== '';
  
  return (
    <div className="p-4 bg-zinc-800/50 border border-white/5">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-4 h-4 text-yellow-500" />
        <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">Auto Scheduling</h4>
      </div>
      
      {/* Auto Go-Live Toggle */}
      <div className="flex items-center justify-between pb-4 border-b border-white/10">
        <div>
          <label className="text-sm text-white font-bold">Auto Go-Live</label>
          <p className="text-xs text-zinc-500">Automatically set broadcast to live at scheduled time</p>
        </div>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, auto_go_live: !autoGoLive })}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            autoGoLive ? 'bg-yellow-600' : 'bg-zinc-700'
          }`}
          data-testid="auto-go-live-toggle"
        >
          <span 
            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              autoGoLive ? 'left-7' : 'left-1'
            }`}
          />
        </button>
      </div>
      
      {/* Auto End-Live Toggle - YouTube Only */}
      <div className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Youtube className="w-4 h-4 text-red-500" />
            <div>
              <label className="text-sm text-white font-bold">Auto End-Live (YouTube)</label>
              <p className="text-xs text-zinc-500">Automatically end broadcast when YouTube stream ends</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, auto_end_enabled: !autoEndEnabled })}
            disabled={!hasYouTubeUrl}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              !hasYouTubeUrl ? 'bg-zinc-800 cursor-not-allowed opacity-50' :
              autoEndEnabled ? 'bg-red-600' : 'bg-zinc-700'
            }`}
            data-testid="auto-end-toggle"
          >
            <span 
              className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                autoEndEnabled ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>
        
        {/* YouTube requirement notice */}
        {!hasYouTubeUrl && (
          <div className="mt-3 p-3 bg-zinc-900/50 border border-zinc-700 text-xs text-zinc-500">
            <strong>YouTube URL required:</strong> Auto End-Live monitors your YouTube stream and automatically 
            ends the broadcast when the stream ends on YouTube. Add a YouTube URL above to enable this feature.
          </div>
        )}
        
        {autoEndEnabled && hasYouTubeUrl && (
          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 text-xs text-red-400">
            <strong>Auto End-Live enabled:</strong> This broadcast will automatically end when your YouTube 
            live stream ends. The system checks every minute for stream status changes.
          </div>
        )}
      </div>
      
      {/* Info box for Auto Go-Live */}
      {autoGoLive && (
        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-400">
          <strong>Auto Go-Live enabled:</strong> This broadcast will automatically go live when the scheduled date/time arrives. 
          Make sure your stream source (YouTube URL or channel) is ready before the scheduled time.
        </div>
      )}

      {/* Dummy Broadcast Toggle */}
      <div className="pt-4 mt-4 border-t border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-violet-400" />
            <div>
              <label className="text-sm text-white font-bold">Dummy Broadcast</label>
              <p className="text-xs text-zinc-500">Only visible to developer account. Hidden from all public pages.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, is_dummy: !isDummy })}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              isDummy ? 'bg-violet-600' : 'bg-zinc-700'
            }`}
            data-testid="dummy-broadcast-toggle"
          >
            <span 
              className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                isDummy ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>
        {isDummy && (
          <div className="mt-3 p-3 bg-violet-500/10 border border-violet-500/30 text-xs text-violet-400">
            <strong>Dummy broadcast:</strong> This broadcast will only be visible to the developer login.
            It won't appear on the schedule, score ticker, or any public-facing page for regular viewers.
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoScheduleSection;
